import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { MapPin, Phone, Route } from 'lucide-react';
import { buildDirectionsUrl, isHomePharmacy } from '../../utils/gardeGeo';
import { parseWeekRange, isCurrentWeek, sortWeeksChronologically } from '../../utils/gardeWeek';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Pharmacy we want to highlight when on duty
const HOME_PHARMACY_NAME_MATCH = /SAINT\s*ANGE\s*DES\s*AMIS/i;

/**
 * Banner shown when our pharmacy is on duty this week.
 * - Detects the pharmacy in the /pharmacy/garde endpoint
 * - Filters to the week containing today's date (if any) else the most recent week
 * - Returns null if we are not currently on duty
 *
 * Props:
 *   onNavigate?: (route: string) => void   // optional CTA to open full garde page
 *   compact?: boolean                      // smaller variant for the home page
 */
const HomePharmacyOnDutyBanner = ({ onNavigate, compact = false }) => {
  const [pharmacy, setPharmacy] = useState(null);
  const [week, setWeek] = useState('');
  const [isCurrent, setIsCurrent] = useState(true);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/pharmacy/garde`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        const all = data.pharmacies || [];
        const allWeeks = [...new Set(all.map((p) => p.week).filter(Boolean))];
        const today = new Date();

        // Detect "current week" — the one whose [start, end] range contains today
        const currentWeek = allWeeks.find((w) => isCurrentWeek(w, today));
        // Sort weeks chronologically by parsed start date
        const sortedWeeks = sortWeeksChronologically(allWeeks, today);

        // Determine candidate weeks: current, then next
        let candidates = [];
        if (currentWeek) {
          const idx = sortedWeeks.indexOf(currentWeek);
          candidates = [
            { week: currentWeek, isCurrent: true },
            ...(sortedWeeks[idx + 1]
              ? [{ week: sortedWeeks[idx + 1], isCurrent: false }]
              : []),
          ];
        } else {
          // No current week match: only show upcoming weeks (start in future)
          candidates = sortedWeeks
            .filter((w) => {
              const r = parseWeekRange(w, today);
              return r && r.start >= today;
            })
            .map((w) => ({ week: w, isCurrent: false }));
        }
        for (const { week: candidateWeek, isCurrent: ic } of candidates) {
          const hit = all.find(
            (p) =>
              HOME_PHARMACY_NAME_MATCH.test(p.name || '') &&
              p.week === candidateWeek
          );
          if (hit) {
            setPharmacy(hit);
            setWeek(hit.week || '');
            setIsCurrent(ic);
            return;
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!pharmacy) return null;

  const sectionLabel = (pharmacy.section || '').replace(
    /^SECTION\s+(D'|DE\s+|DU\s+)/i,
    ''
  );

  const openDirections = () => {
    // Home pharmacy has known-accurate coords — open directly, no warning
    if (isHomePharmacy(pharmacy)) {
      _doOpenDirections();
      return;
    }
    const hasGps = Number.isFinite(pharmacy.lat) && Number.isFinite(pharmacy.lng);
    if (!hasGps) {
      setShowWarning(true);
      return;
    }
    _doOpenDirections();
  };

  const _doOpenDirections = () => {
    const { url } = buildDirectionsUrl(pharmacy);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const gradient = isCurrent
    ? 'from-[#FF6B35] via-[#E63946] to-[#D62828]'
    : 'from-[#1E3A8A] via-[#2563EB] to-[#1E40AF]';
  const accentHalo = isCurrent ? 'bg-[#FFD60A]/20' : 'bg-[#60A5FA]/25';
  const badgeBg = isCurrent ? 'bg-[#FFD60A] text-[#D62828]' : 'bg-white text-[#1E3A8A]';
  const badgeHover = isCurrent ? 'hover:bg-[#FFD60A]' : 'hover:bg-white';
  const phoneText = isCurrent ? 'text-[#D62828]' : 'text-[#1E3A8A]';
  const phoneHoverBg = isCurrent ? 'hover:bg-[#FFD60A]' : 'hover:bg-[#FBBF24]';
  const badgeLabel = isCurrent ? 'Nous sommes de garde' : 'Bientôt de garde';

  return (
    <Card
      className={`bg-gradient-to-r ${gradient} border-0 shadow-2xl overflow-hidden relative cursor-pointer`}
      data-testid="home-pharmacy-banner"
      onClick={() => onNavigate && onNavigate('garde')}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,215,0,0.25),_transparent_60%)]" />
      <div className={`absolute -right-10 -bottom-10 w-48 h-48 ${accentHalo} rounded-full blur-3xl`} />
      <CardContent className={compact ? 'p-4 relative' : 'p-5 md:p-6 relative'}>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="flex items-center gap-3 flex-shrink-0">
            <div
              className={`${
                compact ? 'w-16 h-16' : 'w-20 h-20'
              } bg-white rounded-full flex items-center justify-center shadow-xl ring-4 ring-white/40 flex-shrink-0`}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_654c116d-7b3e-48f0-8086-839f11b37e43/artifacts/t633m3qr_image.png"
                alt="Ouvert 24 heures"
                className={compact ? 'w-12 h-12 object-contain' : 'w-16 h-16 object-contain'}
              />
            </div>
            <div>
              <Badge className={`${badgeBg} ${badgeHover} font-black mb-1 border-0 uppercase tracking-wider text-xs shadow-md`}>
                {badgeLabel}
              </Badge>
              <p className="text-white/95 text-xs font-medium">{week}</p>
            </div>
          </div>
          <div className="flex-1 text-white">
            <h3
              className={`${
                compact ? 'text-lg md:text-xl' : 'text-xl md:text-2xl'
              } font-bold leading-tight drop-shadow`}
            >
              {pharmacy.name}
            </h3>
            <p className="text-white/95 text-sm mt-1 flex items-start gap-1.5">
              <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                {sectionLabel}
                {pharmacy.sub_section && ` › ${pharmacy.sub_section}`}
                {pharmacy.location && ` — ${pharmacy.location}`}
              </span>
            </p>
          </div>
          <div
            className="flex flex-wrap gap-2 md:flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {pharmacy.phones?.slice(0, 2).map((phone, i) => (
              <a
                key={i}
                href={`tel:${phone.replace(/\s/g, '')}`}
                className={`inline-flex items-center gap-2 bg-white ${phoneText} font-bold px-4 py-2 rounded-full ${phoneHoverBg} transition-colors shadow-lg`}
                data-testid="home-pharmacy-call"
              >
                <Phone className="w-4 h-4" />
                {phone}
              </a>
            ))}
            <button
              onClick={openDirections}
              className="inline-flex items-center gap-2 bg-[#1A1A1A]/80 backdrop-blur text-white font-bold px-4 py-2 rounded-full hover:bg-black transition-colors border border-white/40 shadow-lg"
              data-testid="home-pharmacy-directions"
            >
              <Route className="w-4 h-4" />
              Itinéraire
            </button>
          </div>
        </div>
      </CardContent>

      <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
        <AlertDialogContent className="max-w-md" data-testid="banner-directions-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#C45C26] flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7.5a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              Position GPS non vérifiée
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5D4E37] text-sm space-y-3 pt-2">
              <span className="block">
                Google Maps va rechercher <strong>{pharmacy?.name}</strong> par son nom et son adresse.
              </span>
              <span className="block bg-[#FFF8E7] border border-[#D4AF37]/30 rounded-lg p-2.5 text-xs">
                💡 <strong>Conseil</strong> : si le lieu sur Google Maps semble incorrect, appelez-nous avant de vous déplacer.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { _doOpenDirections(); setShowWarning(false); }}
              className="bg-[#C45C26] hover:bg-[#A84B1F]"
            >
              Ouvrir Google Maps
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default HomePharmacyOnDutyBanner;
